<template>
    <div v-if="isLoading"><loader :loading="isLoading" v-if="isLoading" :fixed="true"></loader></div>
    <div v-else-if="forecast && (hasAccessByProfile(PROFILES.FORECAST_MANAGER) || hasAccessByProfile(PROFILES.ZONES_SUPERVISOR))" class="wrapper">
        <loader :loading="isLoading" v-if="isLoading" :fixed="true"></loader>
        <div style="margin-bottom: 5px; display: flex">
            <div><DefaultButton @click="$router.push(`${ROUTES.BASE}`)">&laquo; Back</DefaultButton></div>
            <div style="margin-left: auto">
                <div v-if="hasAccessByProfile(PROFILES.FORECAST_MANAGER) && forecast.status === status.CLOSED">
                    <DefaultButton v-if="!forecast.transmissionApprovalDate" @click="approveTransmission" :size="'l'"> Approve </DefaultButton>
                    <div v-else>
                        <p v-if="forecast.transmissionApprovalBy">
                            Approved by: {{ forecast.transmissionApprovalBy.lastname }}, {{ forecast.transmissionApprovalBy.firstname }}
                        </p>
                        <p>Approved on: {{ formatDate(forecast.transmissionApprovalDate) }}</p>
                    </div>
                </div>
                <div v-if="forecast.status === status.DRAFT && hasAccessByProfile(PROFILES.FORECAST_MANAGER)">
                    <DefaultButton @click="publish(true)" :size="'l'"> Publish Forecast for affiliate zones only </DefaultButton>
                    <DefaultButton v-if="false" @click="publish(false)" :size="'l'">
                        Publish Forecast for all zones (including Export)
                    </DefaultButton>
                </div>
                <div
                    v-if="
                        (hasAccessByProfile(PROFILES.FORECAST_MANAGER) || hasAccessByProfile(PROFILES.ZONES_SUPERVISOR)) &&
                            forecast.status === status.ACTIVE &&
                            forecast.publishMode !== 'all_zones'
                    "
                >
                    <DefaultButton @click="$router.push(`${ROUTES.BASE}/${forecast.id}/secondary-windows`)" :size="'l'">
                        Publish management
                    </DefaultButton>
                </div>
            </div>
        </div>
        <div><ForecastNameBanner :forecast="forecast" /></div>
        <div class="row" style="padding: 10px 0">
            <div class="col-md-4"><ForecastInfos :forecast="forecast" :forecastWindows="forecastWindows" /></div>
            <div class="col-md-8" style="margin-left: auto">
                <div class="actions">
                    <div v-if="canReChallenge">
                        <IconButton
                            @click="isReChallengeModalVisible = true"
                            :background-color="FORECAST_WINDOW_COLORS.RE_CHALLENGED"
                            :height="'53px'"
                            :disabled="!hasForecastWindowsOpen"
                        >
                            <template slot="icon"><ReChallengeIcon /></template>
                            Re-challenge markets
                        </IconButton>
                    </div>
                    <div
                        v-if="
                            (hasAccessByProfile(PROFILES.FORECAST_MANAGER) || hasAccessByProfile(PROFILES.ZONES_SUPERVISOR)) &&
                                forecast.status !== status.DRAFT &&
                                forecast.status !== status.PENDING &&
                                hasAtLeastOneSubmission
                        "
                    >
                        <IconButton @click="exportXls" :height="'53px'"
                            ><template slot="icon"><ExportXlsIcon /></template>
                            Export Forecast Summary
                        </IconButton>
                    </div>
                    <div
                        v-if="hasAccessByProfile(PROFILES.FORECAST_MANAGER)"
                        @mouseenter="isOptionsMenVisible = true"
                        @mouseleave="isOptionsMenVisible = false"
                    >
                        <IconButton :width="'55px'" :icon-only="true">
                            <template slot="icon"><EditIcon /></template>
                        </IconButton>
                        <div v-if="isOptionsMenVisible" class="optionsMenu">
                            <div class="inner">
                                <div v-if="canModify" @click="currentMode = MODES.EDIT" class="item">Modify</div>
                                <div
                                    v-if="ForecastValidator.canDuplicate(forecast) && hasAccessByProfile(PROFILES.FORECAST_MANAGER)"
                                    @click="isDuplicationModalVisible = true"
                                    class="item"
                                >
                                    Duplicate
                                </div>
                                <div class="separation"></div>
                                <div
                                    v-if="ForecastValidator.canDelete(forecast) && hasAccessByProfile(PROFILES.FORECAST_MANAGER)"
                                    @click="deleteForecast"
                                    class="item"
                                >
                                    Delete
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="currentMode === MODES.SHOW" class="corp">
            <!-- - first row - -->
            <div><CalendarInfo :forecast="forecast" /></div>

            <!-- - second row : comments - -->
            <div><Comments v-if="forecast" :forecast="forecast"></Comments></div>

            <!-- - third row : zones - -->
            <div class="SectionBorderTop"><ZonesDetail :forecast="forecast" :forecast-windows="forecastWindows" /></div>

            <!-- - add items and validation - -->
            <ItemManager class="mt-5" :forecast="forecast" :forecast-windows="forecastWindows" />
        </div>
        <ForecastEdit v-if="currentMode === MODES.EDIT && forecast" v-on:close="onEditModalClose" :forecast="forecast" />
        <DuplicationModal
            v-if="isDuplicationModalVisible"
            @closed="isDuplicationModalVisible = false"
            :forecast="forecast"
            @duplicated="onDuplicated"
        />
        <ReChallengeModal
            v-if="isReChallengeModalVisible"
            @closed="isReChallengeModalVisible = false"
            @rechallenged="loadForecastWindows"
            :forecast="forecast"
            :providedForecastWindows="forecastWindows"
        />
    </div>
    <div v-else-if="!forecast && !isLoading">
        <div style="margin-bottom: 5px; display: flex">
            <div><DefaultButton @click="$router.push(`${ROUTES.BASE}`)">&laquo; Back</DefaultButton></div>
        </div>
        <div style="display: flex; justify-content: center">{{ $t("launchForecast.notAvailable") }}</div>
    </div>
</template>
<script>
import ForecastStatus from "@/components/forecasts/elements/ForecastStatus";
import { ROUTES } from "@/components/forecasts/router";
import ClientColorButton from "@/components/ui/ClientColorButton";
import {
    FORECAST_STATUS as status,
    FORECAST_WINDOW_STATUS,
    FUNCTIONALITY,
    RECURRENCE_TYPE as recurrenceTypes
} from "@/components/forecasts/constants";

import { mapActions, mapGetters } from "vuex";
import ForecastCreate from "@/components/forecasts/pages/ForecastCreate";
import ZonesDetail from "@/components/forecasts/pages/sections/ZonesDetail";
import ForecastForm from "@/components/forecasts/elements/ForecastForm";
import ForecastEdit from "@/components/forecasts/pages/ForecastEdit";
import ForecastValidator from "@/components/forecasts/validation/forecastValidator";
import loader from "@/components/loader/loader";
import DuplicationModal from "@/components/forecasts/elements/DuplicationModal";
import { FORECAST_WINDOW_COLORS, getStatusName } from "@/components/forecasts/utils/utilis";
import ReChallengeModal from "@/components/forecasts/elements/ReChallengeModal";
import ItemManager from "@/components/forecasts/pages/sections/ItemManager";
import forecastsService from "@/components/forecasts/forecastsService";
import { PROFILES } from "@/plugins/AccessUserPlugin";
import Globals from "@/components/globals";
import ExportXlsIcon from "@/components/forecasts/elements/icons/ExportXlsIcon";
import StatusIcon from "@/components/forecasts/elements/icons/StatusIcon";
import ViewIcon from "@/components/forecasts/elements/icons/ViewIcon";
import TryLoad from "@/mixins/TryLoadMixin";
import ItemList from "@/components/forecasts/pages/sections/ItemList";
import ForecastNameBanner from "@/components/forecasts/elements/general/ForecastNameBanner";
import ForecastInfos from "@/components/forecasts/elements/general/ForecastInfos";
import DateInfosDetail from "@/components/forecasts/elements/general/DateInfosDetail";
import MultiLineText from "@/components/forecasts/elements/general/MultiLineText";
import DefaultButton from "@/components/forecasts/elements/form/DefaultButton";
import EditIcon from "@/components/forecasts/elements/icons/EditIcon";
import CalendarInfo from "@/components/forecasts/pages/sections/CalendarInfo";
import Comments from "@/components/forecasts/pages/sections/Comments";
import ReChallengeIcon from "@/components/forecasts/elements/icons/ReChallengeIcon";
import IconButton from "@/components/forecasts/elements/form/IconButton";
import ForecastWindowValidator from "@/components/forecasts/validation/forecastWindowValidator";
import { convertForecastInForecastWindow } from "@/components/forecasts/model/forecastWindow";

const MODES = {
    SHOW: "SHOW",
    EDIT: "EDIT"
};

export default {
    components: {
        IconButton,
        Comments,
        CalendarInfo,
        DefaultButton,
        EditIcon,
        MultiLineText,
        DateInfosDetail,
        ForecastInfos,
        ForecastNameBanner,
        ItemList,
        ItemManager,
        ReChallengeModal,
        DuplicationModal,
        ForecastEdit,
        ForecastForm,
        ForecastCreate,
        ForecastStatus,
        ClientColorButton,
        ZonesDetail,
        loader,
        ExportXlsIcon,
        StatusIcon,
        ViewIcon,
        ReChallengeIcon,
        ForecastWindowValidator
    },
    mixins: [TryLoad],
    data() {
        return {
            recurrenceTypes,
            selectedforecastWindowZoneId: null,
            selectedforecastWindowForecast: null,
            status,
            ROUTES,
            FUNCTIONALITY,
            MODES,
            currentMode: MODES.SHOW,
            ForecastValidator,
            forecastId: null,
            isDuplicationModalVisible: false,
            isReChallengeModalVisible: false,
            isLoading: false,
            forecastWindows: [],
            PROFILES,
            isOptionsMenVisible: false,
            FORECAST_WINDOW_COLORS
        };
    },
    computed: {
        forecast() {
            // it's very important to call this getter in a computed property to make sure the getter in the store stays reactive
            // see https://forum.vuejs.org/t/how-to-make-vuex-getters-which-return-function-reactive/28949
            return this.$store.getters["forecast/getForecast"](this.forecastId);
        },
        ...mapGetters("brand", { campaigns: "getCampaigns" }),
        ...mapGetters({ getBrand: "brand/getBrand" }),
        canReChallenge() {
            return this.hasAtLeastOneSubmission && !this.hasOnlyRechallengedWindows && ForecastValidator.canReChallenge(this.forecast);
        },
        hasForecastWindowsOpen() {
            return ForecastWindowValidator.hasForecastWindowsOpen(this.forecastWindows);
        },
        canModify() {
            return this.hasAccessByProfile(PROFILES.FORECAST_MANAGER) && ForecastValidator.canUpdate(this.forecast);
        },
        hasAtLeastOneSubmission() {
            return this.forecastWindows.some(fw => fw.status === FORECAST_WINDOW_STATUS.SUBMITTED);
        },
        hasOnlyRechallengedWindows() {
            return !this.forecastWindows.some(fw => fw.status !== FORECAST_WINDOW_STATUS.RE_CHALLENGED);
        }
    },
    async created() {
        this.isLoading = true;
        await this.populate();
        this.forecastId = this.$route.params.id;
        await this.loadForecastWindows();
        this.isLoading = false;
    },
    methods: {
        ...mapActions("forecast", { populate: "populate" }),
        ...mapActions("forecast", { updateForecast: "updateForecast" }),
        ...mapActions("forecast", { publishForecast: "publishForecast" }),
        ...mapActions("forecast", { deleteForecastFromStore: "deleteForecast" }),
        async loadForecastWindows() {
            this.isLoading = true;
            let response = await forecastsService.getForecastWindowsForForecast(this.forecastId);
            response = await Promise.all(response.map(fw => Promise.resolve(convertForecastInForecastWindow(fw))));
            this.forecastWindows = response.sort((a, b) => {
                return a.zone.name.toLowerCase() > b.zone.name.toLowerCase() ? 1 : -1;
            });
            this.isLoading = false;
        },
        statusName(status) {
            return getStatusName(status.deleted ? "DELETE" : status);
        },
        async publish(onlyAffiliate) {
            this.isLoading = true;
            let currentForecast = await forecastsService.getForecast(this.forecastId);
            this.isLoading = false;
            if (!currentForecast.canPublish) {
                this.dialogError("Please make sure that the forecast contains at least one item and one market and that all items have prices");
            } else {
                const res = await this.dialogConfirm("Do you really want to publish this forecast?", "Publish", "Cancel");
                if (res === true) {
                    this.isLoading = true;
                    this.publishForecast({ forecast: this.forecast, onlyAffiliate })
                        .then(() => {
                            this.isLoading = false;
                            this.$store.commit("forecast/SET_IS_POPULATED", false);
                            this.$router.push(ROUTES.BASE);
                            this.dialogSuccess("Forecast has been published");
                        })
                        .catch(() => {
                            this.$swal({
                                text: "ERROR TO PUBLISH FORECAST",
                                type: "error",
                                confirmButtonText: "OK",
                                confirmButtonColor: "rgb(135, 135, 135)",
                                allowOutsideClick: false,
                                allowEscapeKey: false
                            });
                        });
                }
            }
        },
        async deleteForecast() {
            const res = await this.dialogConfirm("Do you really want to delete this forecast?", "Delete", "Cancel");
            if (res === true) {
                this.isLoading = true;
                try {
                    await this.deleteForecastFromStore(this.forecast);
                    this.$router.push(ROUTES.BASE);
                    this.dialogSuccess("Forecast has been deleted");
                } catch (e) {
                    if (e && e.hasOwnProperty("displayMessage")) {
                        this.dialogError("An error occurred with message: " + e.displayMessage);
                    } else {
                        this.$swal({
                            text: "ERROR TO REMOVED FORECAST",
                            type: "error",
                            confirmButtonText: "OK",
                            confirmButtonColor: "rgb(135, 135, 135)",
                            allowOutsideClick: false,
                            allowEscapeKey: false
                        });
                    }
                } finally {
                    this.isLoading = false;
                }
            }
        },
        onDuplicated(duplicatedForecast) {
            this.$router.push(`${ROUTES.BASE}/${duplicatedForecast.id}`, () => {
                this.forecastId = duplicatedForecast.id;
                location.reload();
            });
        },
        async onEditModalClose(payload) {
            if (payload.updated) {
                await this.loadForecastWindows();
            }
            this.currentMode = MODES.SHOW;
        },
        async approveTransmission() {
            const res = await this.dialogConfirm(
                "You are about to approve the forecast " + this.forecast.reference + ". This action cannot be undone",
                "Confirm",
                "Cancel"
            );
            if (res === true) {
                let result = await forecastsService.approveTransmission(this.forecastId);
                this.forecast.transmissionApprovalDate = result.transmissionApprovalDate;
                this.forecast.transmissionApprovalBy = result.transmissionApprovalBy;
                this.dialogSuccess("Forecast approved successfully");
            }
        },
        async exportXls() {
            this.isLoading = true;
            const resp = await this.tryLoad(() => forecastsService.getPathForExportXLS([this.$route.params.id]));
            const a = document.createElement("a");
            a.href = URL.createObjectURL(new Blob([resp], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
            a.setAttribute(
                "download",
                this.getBrand.name +
                    "_LF-" +
                    this.forecast.reference +
                    "_Summary_" +
                    Globals.moment(new Date()).format("YYYY-MM-DD_HH-mm-ss-SSS") +
                    ".xlsx"
            );
            document.body.appendChild(a);
            a.click();
            this.isLoading = false;
            document.body.removeChild(a);
        }
    }
};
</script>
<style scoped lang="scss">
@import "../css/forecasts";

.wrapper {
    width: 100%;
}
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s ease-in-out;
}

.fade-enter,
.fade-leave-active {
    opacity: 0;
}
.bold {
    font-weight: bold;
}
.CommentText {
    font-size: 0.7em;
    padding-bottom: 6px;
}
.aParent div {
    float: left;
    clear: none;
}
.actions {
    display: flex;
    float: right;
    div {
        padding: 5px;
    }
}
.optionsMenu {
    position: absolute;
    right: 15px;
    background-color: #818181;
    z-index: 100;
    color: #ffffff;
    border-radius: 5px;
    text-transform: uppercase;
    text-align: right;
    min-width: 160px;
    .inner {
        padding: 12px;
        .item {
            cursor: pointer;
            margin-bottom: 3px;
            border-radius: 5px;
            &:hover {
                background-color: #515151;
            }
        }
        .separation {
            border-bottom: 1px dashed #ffffff;
            padding: 5px 0 0 0;
            margin-bottom: 10px;
        }
    }
}
</style>



// WEBPACK FOOTER //
// ForecastDetail.vue?366198ce